import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Tag, X } from 'lucide-react';

const PROMO_CODE = 'SALE50';
const WELCOME_SEEN_KEY = 'redlevel_promo_welcome_seen';
const HARD_RELOAD_KEY = 'redlevel_promo_hard_reload';
const SW_FLAG_CACHE = 'redlevel-promo-flags';
const SW_FLAG_URL = '/__hard_reload_flag';

/** Survives React Strict Mode double-mount within one page load */
let welcomeShowDecision: boolean | null = null;
let welcomeShowPromise: Promise<boolean> | null = null;

const resolveWelcomeShow = (): Promise<boolean> => {
  if (welcomeShowDecision !== null) {
    return Promise.resolve(welcomeShowDecision);
  }
  if (welcomeShowPromise) {
    return welcomeShowPromise;
  }

  welcomeShowPromise = (async () => {
    try {
      if (sessionStorage.getItem(HARD_RELOAD_KEY) === '1') {
        sessionStorage.removeItem(HARD_RELOAD_KEY);
        welcomeShowDecision = true;
        return true;
      }

      // Set by promo-sw.js when browser does a cache-bypass reload
      if ('caches' in window) {
        const cache = await caches.open(SW_FLAG_CACHE);
        const flag = await cache.match(SW_FLAG_URL);
        if (flag) {
          await cache.delete(SW_FLAG_URL);
          welcomeShowDecision = true;
          return true;
        }
      }

      welcomeShowDecision = !localStorage.getItem(WELCOME_SEEN_KEY);
      return welcomeShowDecision;
    } catch {
      welcomeShowDecision = true;
      return true;
    }
  })();

  return welcomeShowPromise;
};

const SALUTE_SYMBOLS = [
  '$',
  '€',
  '₿',
  'Ξ',
  'Ł',
  'Ð',
  '¥',
  '£',
  '₮',
  '◎',
  '₳',
  '✦',
  '💎',
  '🪙',
];

const SALUTE_COLORS = [
  '#f7931a',
  '#627eea',
  '#26a17b',
  '#dc2626',
  '#f0b90b',
  '#14f195',
  '#e84142',
  '#8247e5',
  '#ffffff',
  '#ffd700',
];

interface SaluteParticle {
  id: number;
  symbol: string;
  color: string;
  x: number;
  delay: number;
  duration: number;
  size: number;
  rotate: number;
  drift: number;
}

const createSaluteBurst = (count: number, seed: number): SaluteParticle[] => {
  return Array.from({ length: count }, (_, i) => {
    const n = (seed * 9301 + i * 49297) % 233280;
    const rand = (offset: number) => {
      const v = (n * (offset + 17) + i * 13 * offset) % 1000;
      return v / 1000;
    };
    return {
      id: i,
      symbol: SALUTE_SYMBOLS[i % SALUTE_SYMBOLS.length],
      color: SALUTE_COLORS[i % SALUTE_COLORS.length],
      x: rand(1) * 100,
      delay: rand(2) * 0.55,
      duration: 2.2 + rand(3) * 1.8,
      size: 16 + rand(4) * 28,
      rotate: (rand(5) - 0.5) * 720,
      drift: (rand(6) - 0.5) * 160,
    };
  });
};

const CurrencySalute = () => {
  const particles = useMemo(() => createSaluteBurst(56, Date.now() % 10000), []);

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden z-0" aria-hidden>
      {particles.map((p) => (
        <motion.span
          key={p.id}
          className="absolute font-bold select-none"
          style={{
            left: `${p.x}%`,
            top: '42%',
            color: p.color,
            fontSize: p.size,
            textShadow: `0 0 10px ${p.color}88`,
          }}
          initial={{
            opacity: 0,
            y: 0,
            x: 0,
            scale: 0.2,
            rotate: 0,
          }}
          animate={{
            opacity: [0, 1, 1, 0],
            y: [0, -180 - (p.id % 7) * 28, 320 + (p.id % 5) * 40],
            x: [0, p.drift * 0.4, p.drift],
            scale: [0.2, 1.15, 0.85],
            rotate: [0, p.rotate * 0.5, p.rotate],
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            ease: 'easeOut',
            times: [0, 0.18, 1],
          }}
        >
          {p.symbol}
        </motion.span>
      ))}

      {/* Second wave from bottom corners for a denser salute */}
      {particles.slice(0, 24).map((p) => (
        <motion.span
          key={`b-${p.id}`}
          className="absolute font-bold select-none"
          style={{
            left: p.id % 2 === 0 ? '8%' : '92%',
            bottom: '8%',
            color: p.color,
            fontSize: p.size * 0.85,
            textShadow: `0 0 8px ${p.color}66`,
          }}
          initial={{ opacity: 0, y: 40, x: 0, scale: 0.3, rotate: 0 }}
          animate={{
            opacity: [0, 1, 1, 0],
            y: [40, -220 - (p.id % 6) * 30, -420],
            x: [0, (p.id % 2 === 0 ? 1 : -1) * (60 + (p.id % 8) * 18), (p.id % 2 === 0 ? 1 : -1) * p.drift],
            scale: [0.3, 1.1, 0.7],
            rotate: [0, p.rotate],
          }}
          transition={{
            duration: p.duration + 0.4,
            delay: 0.15 + p.delay,
            ease: 'easeOut',
          }}
        >
          {p.symbol}
        </motion.span>
      ))}
    </div>
  );
};

const PromoOffer = () => {
  const { t } = useTranslation();
  const [showWelcome, setShowWelcome] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const markHardReload = (event: KeyboardEvent) => {
      const isR = event.code === 'KeyR' || event.key === 'r' || event.key === 'R';
      const isF5 = event.code === 'F5' || event.key === 'F5';
      const mod = event.ctrlKey || event.metaKey;
      if ((isR && event.shiftKey && mod) || (isF5 && event.ctrlKey)) {
        try {
          sessionStorage.setItem(HARD_RELOAD_KEY, '1');
        } catch {
          /* ignore */
        }
      }
    };

    window.addEventListener('keydown', markHardReload, true);
    return () => window.removeEventListener('keydown', markHardReload, true);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    resolveWelcomeShow().then((shouldShow) => {
      if (cancelled || !shouldShow) return;
      timer = window.setTimeout(() => setShowWelcome(true), 600);
    });

    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, []);

  const markWelcomeSeen = () => {
    setShowWelcome(false);
    try {
      localStorage.setItem(WELCOME_SEEN_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(PROMO_CODE);
    } catch {
      const input = document.createElement('textarea');
      input.value = PROMO_CODE;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <AnimatePresence>
        {showWelcome && (
          <>
            <motion.div
              key="promo-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={markWelcomeSeen}
              className="fixed inset-0 z-[60] bg-black/75 backdrop-blur-sm"
            />
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 overflow-hidden">
              <CurrencySalute />
              <motion.div
                key="promo-welcome"
                initial={{ opacity: 0, scale: 0.94, y: 16 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.94, y: 16 }}
                className="relative z-10 w-full max-w-md bg-crypto-card border border-crypto-red/35 rounded-3xl p-6 sm:p-8 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={markWelcomeSeen}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors"
                  aria-label={t('promo.close')}
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="w-14 h-14 mb-5 rounded-2xl bg-crypto-red/15 border border-crypto-red/30 flex items-center justify-center">
                  <Tag className="w-7 h-7 text-crypto-red" />
                </div>

                <h3 className="text-2xl font-bold text-white mb-3">{t('promo.welcomeTitle')}</h3>
                <p className="text-gray-400 mb-6 leading-relaxed">{t('promo.welcomeText')}</p>

                <div className="flex items-center gap-3 p-4 rounded-2xl bg-crypto-dark border border-crypto-red/25 mb-6">
                  <code className="flex-1 text-xl font-bold tracking-widest text-white">{PROMO_CODE}</code>
                  <button
                    onClick={copyCode}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-crypto-red hover:bg-crypto-redDark text-white text-sm font-semibold transition-colors"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? t('promo.copied') : t('promo.copy')}
                  </button>
                </div>

                <button
                  onClick={markWelcomeSeen}
                  className="w-full py-3.5 rounded-xl border border-crypto-red/25 text-gray-300 hover:text-white hover:border-crypto-red/50 transition-colors font-medium"
                >
                  {t('promo.close')}
                </button>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {!showWelcome && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.96 }}
            className="fixed bottom-4 right-4 z-40 w-[min(100vw-2rem,280px)]"
          >
            <div className="relative">
              <motion.div
                aria-hidden
                className="pointer-events-none absolute -inset-[3px] rounded-2xl bg-crypto-red"
                animate={{ opacity: [0.35, 0.95, 0.35], scale: [1, 1.02, 1] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                  filter: 'blur(10px)',
                  boxShadow:
                    '0 0 14px 4px rgba(220, 38, 38, 0.9), 0 0 28px 8px rgba(220, 38, 38, 0.55)',
                }}
              />
              <div className="relative bg-crypto-card/95 backdrop-blur-md border border-crypto-red rounded-2xl p-3.5">
                <p className="text-xs text-gray-400 mb-1.5">{t('promo.stickyText')}</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm font-bold tracking-wider text-white">
                    {t('promo.stickyCode')}
                  </code>
                  <button
                    onClick={copyCode}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-crypto-red/90 hover:bg-crypto-red text-white text-xs font-semibold transition-colors"
                  >
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? t('promo.copied') : t('promo.copy')}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PromoOffer;
